import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Settings, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const defaultForm = {
  companyName: '',
  companyEmail: '',
  companyPhone: '',
  companyWebsite: '',
  street: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  taxLabel: 'Tax',
  taxRate: 10,
  currencySymbol: '$',
  currencyCode: 'USD',
  headerNotes: '',
  footerText: 'Thank you for your business!',
  termsText: 'Payment is due within the specified period.',
  paymentInfo: '',
};

export default function AdminInvoiceSettings() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getInvoiceSettings()
      .then(({ data }) => {
        const s = data.data || {};
        setForm({
          companyName: s.companyName || '',
          companyEmail: s.companyEmail || '',
          companyPhone: s.companyPhone || '',
          companyWebsite: s.companyWebsite || '',
          street: s.companyAddress?.street || '',
          city: s.companyAddress?.city || '',
          state: s.companyAddress?.state || '',
          country: s.companyAddress?.country || '',
          postalCode: s.companyAddress?.postalCode || '',
          taxLabel: s.taxLabel || 'Tax',
          taxRate: s.taxRate ?? 10,
          currencySymbol: s.currencySymbol || '$',
          currencyCode: s.currencyCode || 'USD',
          headerNotes: s.headerNotes || '',
          footerText: s.footerText || 'Thank you for your business!',
          termsText: s.termsText || 'Payment is due within the specified period.',
          paymentInfo: s.paymentInfo || '',
        });
      })
      .catch(() => toast.error('Failed to load invoice settings'))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (e) => {
    const { name, value, type } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        companyName: form.companyName,
        companyEmail: form.companyEmail,
        companyPhone: form.companyPhone,
        companyWebsite: form.companyWebsite,
        companyAddress: {
          street: form.street,
          city: form.city,
          state: form.state,
          country: form.country,
          postalCode: form.postalCode,
        },
        taxLabel: form.taxLabel,
        taxRate: form.taxRate,
        currencySymbol: form.currencySymbol,
        currencyCode: form.currencyCode,
        headerNotes: form.headerNotes,
        footerText: form.footerText,
        termsText: form.termsText,
        paymentInfo: form.paymentInfo,
      };
      await adminAPI.updateInvoiceSettings(payload);
      toast.success('Invoice settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sectionHeader = (title) => (
    <div style={{ fontSize: '0.82rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 14, letterSpacing: '0.03em' }}>
      {title}
    </div>
  );

  const field = (label, name, opts = {}) => {
    const { type = 'text', placeholder = '', colSpan, rows } = opts;
    const style = colSpan ? { gridColumn: `span ${colSpan}` } : {};
    return (
      <div style={style}>
        <label className="form-label">{label}</label>
        {rows ? (
          <textarea className="form-input" name={name} value={form[name]} onChange={onChange}
            placeholder={placeholder} rows={rows} style={{ resize: 'vertical' }} />
        ) : (
          <input className="form-input" type={type} name={name} value={form[name]} onChange={onChange}
            placeholder={placeholder} step={type === 'number' ? 'any' : undefined} />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div><h1 className="page-title">Invoice Settings</h1>
            <p className="page-subtitle">Configure header, footer, and defaults for all invoices</p></div>
        </div>
        <div className="page-body"><div className="flex-center" style={{ padding: 60 }}><div className="spinner" /></div></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Settings size={22} style={{ color: 'var(--primary)' }} />
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Invoice Settings</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>Configure header, footer, and defaults for all invoices</p>
          </div>
        </div>
      </div>

      <div className="page-body">
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 780 }}>

          <div className="card">
            {sectionHeader('Company Information')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {field('Company Name', 'companyName', { placeholder: 'Your Company Name', colSpan: 2 })}
              {field('Email', 'companyEmail', { type: 'email', placeholder: 'billing@company.com' })}
              {field('Phone', 'companyPhone', { placeholder: '+1 234 567 890' })}
              {field('Website', 'companyWebsite', { placeholder: 'https://company.com', colSpan: 2 })}
            </div>
          </div>

          <div className="card">
            {sectionHeader('Company Address')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {field('Street', 'street', { placeholder: '123 Main St', colSpan: 2 })}
              {field('City', 'city', { placeholder: 'New York' })}
              {field('State', 'state', { placeholder: 'NY' })}
              {field('Country', 'country', { placeholder: 'United States' })}
              {field('Postal Code', 'postalCode', { placeholder: '10001' })}
            </div>
          </div>

          <div className="card">
            {sectionHeader('Invoice Configuration')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {field('Tax Label', 'taxLabel', { placeholder: 'Tax / VAT / GST' })}
              {field('Tax Rate (%)', 'taxRate', { type: 'number', placeholder: '10' })}
              {field('Currency Symbol', 'currencySymbol', { placeholder: '$' })}
              {field('Currency Code', 'currencyCode', { placeholder: 'USD' })}
            </div>
          </div>

          <div className="card">
            {sectionHeader('Custom Content')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {field('Header Notes', 'headerNotes', { rows: 3, placeholder: 'Shown below company info on the invoice...' })}
              {field('Footer Text', 'footerText', { rows: 3, placeholder: 'Thank you for your business!' })}
              {field('Terms & Conditions', 'termsText', { rows: 4, placeholder: 'Payment is due within 30 days...' })}
              {field('Payment Information', 'paymentInfo', { rows: 4, placeholder: 'Bank: ABC Bank\nAccount: 1234567890\nRouting: 021000021' })}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
